import { useState } from "react";
import { Button, Form, ButtonGroup } from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import httpRequest from "../HttpRequestService";
import { formatDate3 } from "../function";

const STATUS_LABELS = {
    pending:    'En attente',
    checking:   'Vérification',
    processing: 'En cours',
    completed:  'Complété',
    canceled:   'Annulé',
    expired:    'Expiré',
    failed:     'Échoué',
};

const StatusBadge = ({ status }) => (
    <span className={`badge-status badge-${status}`}>
        {STATUS_LABELS[status] || status}
    </span>
);

const TypeBadge = ({ type }) => (
    <span className={`badge-status badge-${type}`}>
        {type === 'depot' ? '⬇ Dépôt' : '⬆ Retrait'}
    </span>
);

const Trx = ({ dt, load: reload }) => {
    const [token] = useOutletContext();
    const [ref, setRef] = useState(dt.txId || '');
    const [loading, setLoading] = useState(false);

    const doRequest = async (endpoint, body) => {
        try {
            await httpRequest(`${token}/trx/${endpoint}`, body, false);
            reload();
        } catch (error) {
            console.error(error.response?.data);
            reload();
        }
    };

    const Confirmer = (type) => doRequest('confirm', { ref, type, trxId: dt._id });
    const Annuler   = () => doRequest('cancel',  { trxId: dt._id });
    const Reactiver = () => doRequest('reactive', { trxId: dt._id });

    const RelancerUssd = async () => {
        setLoading(true);
        try {
            await httpRequest(`${token}/trx/reactiveUssd`, { trxId: dt._id }, false);
            reload();
        } catch (error) {
            console.error(error.response?.data);
            reload();
        } finally {
            setLoading(false);
        }
    };

    const copyToclipboard = () => {
        const input = document.createElement("input");
        input.value = dt.preUssd;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
    };

    const canAction = !['completed', 'expired', 'failed', 'canceled'].includes(dt.status);
    const canConfirm = dt.status === 'pending' || dt.status === 'checking';
    const canAddRef  = dt.status === 'completed' && !dt.txId;

    return (
        <tr>
            {/* USSD message + relancer */}
            <td style={{ maxWidth: '180px' }}>
                {!loading && dt.status === 'pending' && dt.type === 'retrait' && dt._ussd && !dt._ussd.isPending && (
                    <Button variant="outline-danger" size="sm" className="mb-1 d-block" onClick={RelancerUssd}>
                        ↺ Relancer
                    </Button>
                )}
                {loading && <span className="text-muted" style={{ fontSize: '12px' }}>Envoi...</span>}
                <small style={{ color: '#6c757d', wordBreak: 'break-word' }}>
                    {dt._ussd?.message || '—'}
                </small>
            </td>

            {/* Session */}
            <td>
                <code style={{ fontSize: '11px', background: '#f4f7ff', padding: '2px 6px', borderRadius: '4px', color: '#1e3a5f' }}>
                    {dt.public_id}
                </code>
            </td>

            {/* API */}
            <td style={{ fontSize: '12px', color: '#6c757d' }}>{dt.apiName}</td>

            {/* Date */}
            <td style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>{formatDate3(dt.createdAt)}</td>

            {/* Type */}
            <td><TypeBadge type={dt.type} /></td>

            {/* Paiement */}
            <td>
                <div className="amount-display">{dt.amount?.toLocaleString('fr')} Ar</div>
                {dt.meta_data?.find(m => m.key === 'phoneNumber')?.value && (
                    <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                        📱 {dt.meta_data.find(m => m.key === 'phoneNumber').value}
                    </div>
                )}
                {dt.txId && (
                    <div style={{ fontSize: '11px', color: '#6c757d' }}>Réf: {dt.txId}</div>
                )}
            </td>

            {/* Statut */}
            <td><StatusBadge status={dt.status} /></td>

            {/* Order ID */}
            <td style={{ fontSize: '12px', color: '#6c757d', maxWidth: '100px', wordBreak: 'break-all' }}>
                {dt.orderId || '—'}
            </td>

            {/* Passerelle */}
            <td style={{ fontSize: '12px' }}>
                {dt.passerelle?.map((p, i) => (
                    <div key={i} style={{ color: p ? '#1e3a5f' : '#adb5bd' }}>
                        {p ? p.name : '—'}
                    </div>
                ))}
            </td>

            {/* PreCode */}
            <td>
                {dt.type === 'retrait' && (
                    <Button variant="outline-secondary" size="sm" onClick={copyToclipboard} title="Copier le code">
                        📋
                    </Button>
                )}
            </td>

            {/* Actions */}
            <td style={{ minWidth: '160px' }}>
                {(canConfirm || canAddRef) && (
                    <Form.Control
                        size="sm"
                        placeholder="Référence"
                        value={ref}
                        onChange={(e) => setRef(e.target.value)}
                        style={{ marginBottom: '6px', fontSize: '12px' }}
                    />
                )}
                <ButtonGroup size="sm" className="flex-wrap" style={{ gap: '4px' }}>
                    {canAddRef && (
                        <Button variant="info" onClick={() => Confirmer('addRef')}>+ Réf</Button>
                    )}
                    {canConfirm && (
                        <Button variant="success" onClick={() => Confirmer('confirm')}>✓ Confirmer</Button>
                    )}
                    {canAction && (
                        <Button variant="outline-danger" onClick={Annuler}>✕ Annuler</Button>
                    )}
                    {dt.status === 'expired' && (
                        <Button variant="warning" onClick={Reactiver}>↺ Réactiver</Button>
                    )}
                </ButtonGroup>
            </td>
        </tr>
    );
};

export default Trx;
